import { useEffect, useState } from 'react'
import Button from '@components/ui/Button'
import Input from '@components/ui/Input'
import { listRules, attachRuleToScenario } from '@services/ruleService'
import { getLatestMonthSummary } from '@services/payrollService'
import MultiSelectAutocomplete from '@components/ui/MultiSelectAutocomplete'
import { useFilteredDimensions } from '@hooks/useFilteredDimensions'
import { Stage, Layer, Rect, Text, Group } from 'react-konva'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts'
import { createScenario, runScenario, updateScenario } from '@services/scenarioService'
import { useGlobalFilters } from '@contexts/FilterContext'

export default function SimulatorPage() {
  const { monthYear, filters: globalFilters } = useGlobalFilters()
  const [rules, setRules] = useState([])
  const [selectedRules, setSelectedRules] = useState({})
  const [scenario, setScenario] = useState({ name: '', description: '', calculation_base: 'gross', reference_month: '' })
  const [createdScenario, setCreatedScenario] = useState(null)
  const [result, setResult] = useState(null)
  const [filterType, setFilterType] = useState('')
  const [filterText, setFilterText] = useState('')
  const [sortKey, setSortKey] = useState('increase')
  const [sortDir, setSortDir] = useState('desc')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [nodes, setNodes] = useState([]) // canvas nodes: {id, ruleId, name, x, y}
  const [filters, setFilters] = useState(globalFilters || { grupo: [], cargo: [], fundo: [], regime: [], grupo_evento: [], evento: [], situacao: [] })

  // Usa o hook para dimensões filtradas
  const { dimensions: dims } = useFilteredDimensions(filters, monthYear || scenario.reference_month)

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function load() {
      try {
        const data = await listRules({ is_active: true }, abort.signal)
        if (!active) return
        setRules(data)
      } catch (e) {
        if (abort.signal.aborted || e?.name === 'AbortError') return
        if (!active) return
        setError(e.message)
      }
    }
    load()
    return () => {
      active = false
      abort.abort()
    }
  }, [])

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function presetMonth() {
      try {
        const data = await getLatestMonthSummary(abort.signal)
        const mm = data?.month_year || ''
        if (!active) return
        setScenario((prev) => ({ ...prev, reference_month: prev.reference_month || mm }))
      } catch (e) {
      }
    }
    presetMonth()
    return () => { active = false; abort.abort() }
  }, [])

  const toggleRule = (r) => {
    setSelectedRules((prev) => {
      const next = { ...prev }
      if (next[r.id]) delete next[r.id]
      else next[r.id] = { custom_increase_type: r.increase_type, custom_increase_value: r.increase_value }
      return next
    })
  }

  const addNodeToCanvas = (r) => {
    setNodes((prev) => ([
      ...prev,
      { id: `${r.id}-${Date.now()}`, ruleId: r.id, name: r.name, x: 40 + (prev.length % 5) * 130, y: 40 + Math.floor(prev.length / 5) * 80 }
    ]))
    // Também marca como selecionada para anexar ao cenário
    setSelectedRules((prev) => ({ ...prev, [r.id]: prev[r.id] || { custom_increase_type: r.increase_type, custom_increase_value: r.increase_value } }))
  }

  const onCreateScenario = async () => {
    if (!scenario.name.trim()) return alert('Informe um nome para o cenário')
    try {
      setLoading(true)
      // Salva layout no cenário
      const layout = nodes.map(n => ({ ruleId: n.ruleId, x: n.x, y: n.y }))
      const s = await createScenario({ ...scenario, layout_json: layout })
      setCreatedScenario(s)
      // anexar regras selecionadas com order_index baseado no X
      const byX = [...nodes]
        .sort((a, b) => a.x - b.x)
        .map((n, idx) => ({ ruleId: n.ruleId, order_index: idx }))
      const orderMap = new Map(byX.map(o => [o.ruleId, o.order_index]))
      const entries = Object.entries(selectedRules)
      let nextIndex = byX.length
      for (const [ruleIdStr, cfg] of entries) {
        const ruleId = Number(ruleIdStr)
        const order_index = orderMap.has(ruleId) ? orderMap.get(ruleId) : nextIndex++
        await attachRuleToScenario({ scenario: s.id, rule_id: ruleId, order_index, ...cfg })
      }
      alert('Cenário criado e regras anexadas.')
    } catch (e) {
      alert('Erro ao criar cenário: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const onRun = async () => {
    if (!createdScenario) return alert('Crie o cenário antes de executar')
    try {
      setLoading(true)
      // Monta filtros: usa descrições no campo 'evento' (não códigos)
      const filt = {}
      Object.entries(filters).forEach(([k, arr]) => {
        if (Array.isArray(arr) && arr.length) filt[k] = arr
      })
      const res = await runScenario(createdScenario.id, { base_salary_pool: 0, total_increase_percentage: 0, month_year: monthYear, filters: filt })
      setResult(res)
    } catch (e) {
      alert('Erro ao executar simulação: ' + e.message)
    } finally {
      setLoading(false)
    }
  }

  const filteredBreakdown = (() => {
    const rows = result?.simulation_data?.breakdown || []
    let out = rows
    if (filterType) {
      out = out.filter((b) => (b.type || '').toLowerCase().includes(filterType.toLowerCase()))
    }
    if (filterText) {
      out = out.filter((b) => {
        const target = b.target || b.group || b.position || ''
        return String(target).toLowerCase().includes(filterText.toLowerCase())
      })
    }
    const key = sortKey
    out = [...out].sort((a, b) => {
      const av = Number(a[key] ?? (key === 'target' ? a.target || a.group || a.position || '' : 0))
      const bv = Number(b[key] ?? (key === 'target' ? b.target || b.group || b.position || '' : 0))
      if (isNaN(av) || isNaN(bv)) {
        const as = String(a.target || a.group || a.position || '')
        const bs = String(b.target || b.group || b.position || '')
        return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as)
      }
      return sortDir === 'asc' ? av - bv : bv - av
    })
    return out
  })()

  const exportJSON = () => {
    const data = filteredBreakdown
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `breakdown_${result?.scenario_name || 'cenario'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const exportCSV = () => {
    const rows = filteredBreakdown
    const headers = ['type','target','base','before','increase','after','employees','rule']
    const csv = [headers.join(',')].concat(
      rows.map((r) => [r.type, (r.target || r.group || r.position || ''), r.base, r.before, r.increase, r.after, r.employees ?? '', r.rule].join(','))
    ).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `breakdown_${result?.scenario_name || 'cenario'}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Simulador</h2>
        <p className="text-gray-600">Crie cenários e adicione regras para simular aumentos.</p>
      </div>

      <div className="card-contrast p-4 space-y-4">
        <h3 className="font-semibold">Cenário</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input label="Nome" value={scenario.name} onChange={(e) => setScenario({ ...scenario, name: e.target.value })} />
          <FormControl fullWidth>
            <InputLabel id="calc-base-label">Base de Cálculo</InputLabel>
            <Select labelId="calc-base-label" label="Base de Cálculo" value={scenario.calculation_base} onChange={(e) => setScenario({ ...scenario, calculation_base: e.target.value })}>
              <MenuItem value="gross">Bruto</MenuItem>
              <MenuItem value="net">Líquido</MenuItem>
            </Select>
          </FormControl>
          <Input label="Mês de Referência (YYYY-MM)" placeholder="2025-01" value={scenario.reference_month} onChange={(e) => setScenario({ ...scenario, reference_month: e.target.value })} />
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Descrição</label>
            <textarea className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600" rows={3} value={scenario.description} onChange={(e) => setScenario({ ...scenario, description: e.target.value })} />
          </div>
        </div>
        <div className="flex gap-3">
          <Button onClick={onCreateScenario} isLoading={loading}>Criar Cenário</Button>
          <Button variant="outline" onClick={onRun} disabled={!createdScenario || loading}>Executar Simulação</Button>
        </div>
      </div>

      <div className="card-contrast p-4 space-y-3">
        <h3 className="font-semibold">Filtros da Simulação</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Grupo</span></div>
            <MultiSelectAutocomplete label="" options={dims.grupos || []} value={filters.grupo} onChange={(val)=> setFilters(prev => ({...prev, grupo: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Cargo</span></div>
            <MultiSelectAutocomplete label="" options={dims.cargos || []} value={filters.cargo} onChange={(val)=> setFilters(prev => ({...prev, cargo: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Fundo</span></div>
            <MultiSelectAutocomplete label="" options={dims.fundos || []} value={filters.fundo} onChange={(val)=> setFilters(prev => ({...prev, fundo: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Regime</span></div>
            <MultiSelectAutocomplete label="" options={dims.regimes || []} value={filters.regime} onChange={(val)=> setFilters(prev => ({...prev, regime: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Grupo do Evento</span></div>
            <MultiSelectAutocomplete label="" options={dims.grupos_evento || []} value={filters.grupo_evento} onChange={(val)=> setFilters(prev => ({...prev, grupo_evento: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Evento</span></div>
            <MultiSelectAutocomplete label="" options={dims.descricoes_evento || []} value={filters.evento} onChange={(val)=> setFilters(prev => ({...prev, evento: val}))} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><span className="text-xs text-gray-500">Situação</span></div>
            <MultiSelectAutocomplete label="" options={dims.situacoes || []} value={filters.situacao} onChange={(val)=> setFilters(prev => ({...prev, situacao: val}))} />
          </div>
        </div>
      </div>

      <div className="card-contrast p-4 space-y-3">
        <h3 className="font-semibold">Regras disponíveis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {rules.map((r) => (
            <label key={r.id} className="rounded-xl shadow-soft p-3 flex items-start gap-3">
              <FormControlLabel control={<Checkbox checked={!!selectedRules[r.id]} onChange={() => toggleRule(r)} />} label="" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-xs text-gray-500">{r.increase_type === 'percentage' ? `${r.increase_value}%` : `R$ ${Number(r.increase_value).toLocaleString('pt-BR')}`}</span>
                </div>
                <p className="text-sm text-gray-600">{r.description || '—'}</p>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => addNodeToCanvas(r)}>Adicionar ao Canvas</Button>
                </div>
                {selectedRules[r.id] && (
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <FormControl size="small">
                      <InputLabel id={`tipo-${r.id}`}>Tipo personalizado</InputLabel>
                      <Select labelId={`tipo-${r.id}`} label="Tipo personalizado" value={selectedRules[r.id].custom_increase_type} onChange={(e) => setSelectedRules(prev => ({ ...prev, [r.id]: { ...prev[r.id], custom_increase_type: e.target.value } }))}>
                        <MenuItem value="percentage">Percentual</MenuItem>
                        <MenuItem value="fixed">Valor fixo</MenuItem>
                      </Select>
                    </FormControl>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Valor personalizado</label>
                      <input className="input input-bordered input-sm w-full" type="number" step="0.01" value={selectedRules[r.id].custom_increase_value} onChange={(e) => setSelectedRules(prev => ({ ...prev, [r.id]: { ...prev[r.id], custom_increase_value: e.target.value } }))} />
                    </div>
                  </div>
                )}
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Canvas interativo para organizar regras do cenário */}
      <div className="card-contrast p-4 space-y-3">
        <h3 className="font-semibold">Canvas do Cenário (arraste para organizar)</h3>
        <div className="w-full overflow-x-auto">
          <Stage width={1024} height={420} className="rounded">
            <Layer>
              <Text x={10} y={8} text={scenario.name ? `Cenário: ${scenario.name}` : 'Cenário: (sem nome)'} fontSize={14} fill="#555" />
              {nodes.map((n, idx) => (
                <Group key={n.id} x={n.x} y={n.y} draggable
                  onDragEnd={(e) => {
                    const { x, y } = e.target.position()
                    setNodes((prev) => prev.map((p) => p.id === n.id ? { ...p, x, y } : p))
                  }}
                >
                  <Rect width={120} height={50} fill="#F3E5FF" stroke="#6107F3" strokeWidth={1.5} cornerRadius={8} shadowBlur={4} />
                  <Text x={8} y={8} text={n.name} fontSize={12} fill="#2d2d2d" width={104} height={34} />
                </Group>
              ))}
            </Layer>
          </Stage>
        </div>
        <p className="text-xs text-gray-500">Dica: use “Adicionar ao Canvas” para posicionar regras. A ordem visual ajuda a priorizar mentalmente; a execução seguirá as prioridades definidas nas regras.</p>
        {createdScenario && (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const layout = nodes.map(n => ({ ruleId: n.ruleId, x: n.x, y: n.y }))
                  await updateScenario(createdScenario.id, { layout_json: layout })
                  alert('Layout salvo no cenário.')
                } catch (e) {
                  alert('Erro ao salvar layout: ' + e.message)
                }
              }}
            >Salvar Layout</Button>
          </div>
        )}
      </div>

      {result && (
        <div className="bg-white rounded-lg border p-4 space-y-4">
          <div>
            <h3 className="font-semibold">Resultado</h3>
            <p><strong>Cenário:</strong> {result.scenario_name}</p>
            <p><strong>Impacto Total:</strong> R$ {Number(result.total_impact || 0).toLocaleString('pt-BR')}</p>
            <p><strong>Afetados:</strong> {result.affected_employees_count}</p>
          </div>

          <div>
            <div className="flex items-end justify-between mb-2 gap-3 flex-wrap">
              <div>
                <h4 className="font-medium">Antes/Depois por alvo</h4>
                <p className="text-xs text-gray-500">Filtre por tipo (group, position, fund, regime, event, event_desc) e alvo.</p>
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Input label="Tipo" placeholder="group / position / ..." value={filterType} onChange={(e) => setFilterType(e.target.value)} />
                </div>
                <div>
                  <Input label="Alvo" placeholder="buscar alvo..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                </div>
                <div>
                  <FormControl size="small">
                    <InputLabel id="sort-key-label">Ordenar</InputLabel>
                    <Select labelId="sort-key-label" label="Ordenar" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                      <MenuItem value="increase">Aumento</MenuItem>
                      <MenuItem value="before">Antes</MenuItem>
                      <MenuItem value="after">Depois</MenuItem>
                      <MenuItem value="employees">Funcionários</MenuItem>
                      <MenuItem value="target">Alvo</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <div>
                  <FormControl size="small">
                    <InputLabel id="sort-dir-label">Direção</InputLabel>
                    <Select labelId="sort-dir-label" label="Direção" value={sortDir} onChange={(e) => setSortDir(e.target.value)}>
                      <MenuItem value="desc">Desc</MenuItem>
                      <MenuItem value="asc">Asc</MenuItem>
                    </Select>
                  </FormControl>
                </div>
                <Button size="sm" variant="outline" onClick={exportCSV}>Exportar CSV</Button>
                <Button size="sm" onClick={exportJSON}>Exportar JSON</Button>
              </div>
            </div>
            {/* Tabela simples + gráfico comparativo */}
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full text-sm">
                <thead>
                  <tr className="text-left">
                    <th>Tipo</th>
                    <th>Alvo</th>
                    <th>Base</th>
                    <th className="text-right">Antes</th>
                    <th className="text-right">Aumento</th>
                    <th className="text-right">Depois</th>
                    <th className="text-right">Funcionários</th>
                    <th>Regra</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBreakdown.map((b, idx) => (
                    <tr key={idx} className={b.type === 'parity' ? 'bg-purple-50/50 dark:bg-purple-900/20 border-l-4 border-purple-500' : ''}>
                      <td>
                        {b.type === 'parity' && <span className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 dark:text-purple-300">⚖️ Paridade</span>}
                        {b.type !== 'parity' && b.type}
                      </td>
                      <td>{b.target || b.group || b.position || '—'}</td>
                      <td>{b.base}</td>
                      <td className="text-right">R$ {Number(b.before).toLocaleString('pt-BR')}</td>
                      <td className="text-right">
                        <span className={b.type === 'parity' ? 'text-purple-600 dark:text-purple-400 font-semibold' : ''}>
                          R$ {Number(b.increase).toLocaleString('pt-BR')}
                        </span>
                      </td>
                      <td className="text-right">R$ {Number(b.after).toLocaleString('pt-BR')}</td>
                      <td className="text-right">{b.employees ?? '—'}</td>
                      <td>
                        {b.rule}
                        {b.parity_from && (
                          <div className="text-xs text-purple-600 dark:text-purple-400">
                            ⚖️ de: {b.parity_from}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Gráfico: comparação Antes x Depois (agrupado por alvo) */}
            <div className="h-96 mt-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={(() => {
                  const m = {}
                  filteredBreakdown.forEach((b) => {
                    const key = b.target || b.group || b.position || '—'
                    if (!m[key]) m[key] = { target: key, before: 0, after: 0 }
                    m[key].before += Number(b.before) || 0
                    m[key].after += Number(b.after) || 0
                  })
                  return Object.values(m).slice(0, 20)
                })()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="target" interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis domain={[0, 'dataMax']} tickFormatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`} width={100} />
                  <Tooltip formatter={(v) => `R$ ${Number(v).toLocaleString('pt-BR')}`} />
                  <Legend />
                  <Bar dataKey="before" name="Antes" fill="#60a5fa" />
                  <Bar dataKey="after" name="Depois" fill="#34d399" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
